<script setup lang="ts">
// The call, small, in a corner while you browse elsewhere in the app: who's talking, mute and
// leave, and a tap back to the call's conversation. Drag it to another corner, or collapse it
// to its bar; both are remembered on this device.
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { bottomInset } from '../lib/bottomBar'
import { dmTitle } from '../lib/format'
import { useCall, type CallTile } from '../stores/call'
import { useSession } from '../stores/session'
import Avatar from './Avatar.vue'
import Icon from './Icon.vue'
import TrackView from './TrackView.vue'

const call = useCall()
const session = useSession()
const route = useRoute()
const router = useRouter()

/** Hidden in the call's own conversation, where the call panel already is. */
const show = computed(() => call.state === 'connected' && !!call.conv && route.params.convId !== call.conv)

const where = computed(() => {
  const c = call.conv ? session.conversations.get(call.conv) : undefined
  if (!c) return { label: 'Call', to: '/' }
  if (c.kind === 'channel') {
    const sp = session.spaces.find((s) => s.id === c.space_id)
    return { label: sp ? `${sp.name} › #${c.name ?? ''}` : `#${c.name ?? ''}`, to: `/s/${c.space_id ?? ''}/${c.id}` }
  }
  return { label: dmTitle(session.profiles, c.members, session.me, c.name), to: `/dm/${c.id}` }
})

// Who to show: whoever spoke last stays up until someone else speaks.
const lastSpeaker = ref<string | null>(null)
watch(
  () => call.members.find((m) => !m.local && m.speaking && m.micOn)?.identity,
  (id) => {
    if (id) lastSpeaker.value = id
  },
)
const tile = computed<CallTile | null>(() => {
  const ts = call.tiles
  return (
    ts.find((t) => t.kind === 'screen' && !t.member.local) ??
    ts.find((t) => t.kind === 'camera' && t.member.identity === lastSpeaker.value) ??
    ts.find((t) => !t.member.local && t.track) ??
    ts.find((t) => !t.member.local) ??
    ts[0] ??
    null
  )
})

const COLLAPSED_KEY = 'hrth:call-mini:collapsed'
const CORNER_KEY = 'hrth:call-mini:corner'
type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
const CORNERS: Corner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right']

const collapsed = ref(localStorage.getItem(COLLAPSED_KEY) === '1')
watch(collapsed, (v) => localStorage.setItem(COLLAPSED_KEY, v ? '1' : '0'))
const stored = localStorage.getItem(CORNER_KEY) as Corner | null
const corner = ref<Corner>(stored && CORNERS.includes(stored) ? stored : 'bottom-right')
watch(corner, (v) => localStorage.setItem(CORNER_KEY, v))

const MARGIN = 12
const placement = computed(() => {
  const [y, x] = corner.value.split('-') as ['top' | 'bottom', 'left' | 'right']
  return { [x]: `${MARGIN}px`, [y]: `${y === 'bottom' ? bottomInset.value + MARGIN : MARGIN}px` }
})

// Dragging: the card follows the pointer, then settles in the nearest corner. A press that
// barely moves is a tap (back to the call), not a drag.
const root = ref<HTMLElement | null>(null)
const offset = ref<{ x: number; y: number } | null>(null)
let start: { x: number; y: number; id: number } | null = null
let dragged = false

function down(e: PointerEvent): void {
  if (e.button !== 0) return
  start = { x: e.clientX, y: e.clientY, id: e.pointerId }
  dragged = false
}
function move(e: PointerEvent): void {
  if (!start || e.pointerId !== start.id) return
  const dx = e.clientX - start.x
  const dy = e.clientY - start.y
  if (!dragged && Math.hypot(dx, dy) < 6) return
  if (!dragged) (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  dragged = true
  offset.value = { x: dx, y: dy }
}
function up(e: PointerEvent): void {
  if (!start || e.pointerId !== start.id) return
  start = null
  if (!dragged || !root.value) return
  const r = root.value.getBoundingClientRect()
  const cx = r.left + r.width / 2
  const cy = r.top + r.height / 2
  corner.value = `${cy < window.innerHeight / 2 ? 'top' : 'bottom'}-${cx < window.innerWidth / 2 ? 'left' : 'right'}`
  offset.value = null
}
function cancel(): void {
  start = null
  offset.value = null
}

function back(): void {
  if (dragged) {
    dragged = false
    return
  }
  void router.push(where.value.to)
}
</script>

<template>
  <div
    v-if="show"
    ref="root"
    class="panel fixed z-30 flex w-56 touch-none flex-col border-border-strong shadow-lg select-none md:w-64"
    :class="offset ? 'cursor-grabbing' : 'transition-[top,bottom,left,right] duration-200'"
    :style="{ ...placement, transform: offset ? `translate(${offset.x}px, ${offset.y}px)` : undefined }"
    role="region"
    aria-label="Ongoing call"
    data-testid="call-mini"
    :data-corner="corner"
    @pointerdown="down"
    @pointermove="move"
    @pointerup="up"
    @pointercancel="cancel"
  >
    <button
      v-if="!collapsed"
      class="relative block aspect-video w-full cursor-pointer overflow-hidden bg-black"
      :title="`Back to the call in ${where.label}`"
      data-testid="call-mini-open"
      @click="back"
    >
      <div v-if="tile" class="pointer-events-none flex h-full w-full items-center justify-center">
        <TrackView v-if="tile.track" :track="tile.track" :mirror="tile.member.local && tile.kind === 'camera'" :fit="tile.kind === 'screen' ? 'contain' : 'cover'" />
        <Avatar v-else :id="tile.member.userId" :name="tile.member.name" :size="48" />
      </div>
      <span class="mono absolute bottom-0 left-0 max-w-full truncate bg-black/70 px-1.5 py-0.5 text-white">
        <template v-if="tile">{{ tile.member.name }}{{ tile.kind === 'screen' ? ' · screen' : '' }}</template>
      </span>
    </button>
    <div class="flex items-center gap-1 px-2 py-1.5" :class="collapsed ? '' : 'border-t border-border'">
      <button class="mono flex min-w-0 flex-1 items-center gap-1.5 text-left uppercase hover:text-text-primary" @click="back">
        <span class="h-1.5 w-1.5 shrink-0 animate-blink bg-signal" aria-hidden="true" />
        <span class="truncate" data-testid="call-mini-where">{{ where.label }}</span>
        <span class="shrink-0 text-text-muted">{{ String(call.members.length).padStart(2, '0') }}</span>
      </button>
      <button
        class="btn-ghost shrink-0 px-1.5"
        :class="call.mic ? '' : 'text-signal'"
        :aria-pressed="!call.mic"
        :aria-label="call.mic ? 'Mute' : 'Unmute'"
        :title="call.mic ? 'Mute' : 'Unmute'"
        data-testid="call-mini-mic"
        @click="call.toggleMic()"
      >
        <Icon :name="call.mic ? 'mic' : 'mic-off'" />
      </button>
      <button class="btn-danger shrink-0 px-1.5" aria-label="Leave" title="Leave" data-testid="call-mini-leave" @click="call.leave()">
        <Icon name="hangup" />
      </button>
      <button
        class="btn-ghost shrink-0 px-1.5"
        :aria-label="collapsed ? 'Show the call' : 'Collapse'"
        :title="collapsed ? 'Show the call' : 'Collapse'"
        :aria-expanded="!collapsed"
        data-testid="call-mini-collapse"
        @click="collapsed = !collapsed"
      >
        <Icon name="down" :class="collapsed ? 'rotate-180' : ''" />
      </button>
    </div>
  </div>
</template>
